import React, { useState, useEffect } from 'react';
import { useColorTheme } from '../../context/ColorThemeContext';

const URLCopyModal = ({ 
  isOpen, 
  onClose, 
  url, 
  title = "Link Copied!",
  description = "Share this link with others to get feedback on your campaign:"
}) => {
  const { colorTheme } = useColorTheme();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (isOpen && url) {
      // Auto-close after 5 seconds
      const timer = setTimeout(() => {
        onClose();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isOpen, url, onClose]);

  const handleCopyAgain = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const handleOpenInNewTab = () => {
    window.open(url, '_blank');
  };

  if (!isOpen || !url) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-md mx-4 border-2 ${colorTheme === 'purple' ? 'border-purple-200 dark:border-purple-700' : 'border-green-200 dark:border-green-700'} relative`}>
        {/* Background glow effect */}
        <div className={`absolute inset-0 bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-50 via-pink-50 to-purple-50 dark:from-purple-900/20 dark:via-pink-900/20 dark:to-purple-900/20' : 'from-green-50 via-blue-50 to-purple-50 dark:from-green-900/20 dark:via-blue-900/20 dark:to-purple-900/20'} rounded-2xl`}></div>
        
        {/* Header */}
        <div className="relative text-center mb-6">
          <div className="inline-flex items-center space-x-3 mb-3">
            <div className="text-4xl">🔗</div>
            <h2 className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${colorTheme === 'purple' ? 'from-purple-500 via-pink-500 to-purple-500' : 'from-green-500 via-blue-500 to-purple-500'}`}>
              {title}
            </h2>
            <div className="text-4xl">✨</div>
          </div>
        </div>

        {/* Success Icon */}
        <div className="relative flex justify-center mb-6">
          <div className={`relative w-20 h-20 rounded-full flex items-center justify-center text-4xl bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-400 via-purple-500 to-purple-600' : 'from-green-400 via-green-500 to-green-600'} text-white shadow-2xl`}>
            📋
            
            {/* Glow effects */}
            <div className={`absolute inset-0 rounded-full bg-gradient-to-br ${colorTheme === 'purple' ? 'from-purple-400 via-purple-500 to-purple-600' : 'from-green-400 via-green-500 to-green-600'} opacity-40 animate-pulse`}></div>
            
            {/* Ring effect */}
            <div className={`absolute -inset-3 rounded-full border-3 ${colorTheme === 'purple' ? 'border-purple-400' : 'border-green-400'} animate-spin`}></div>
          </div>
        </div>

        {/* Description */}
        <div className="relative text-center mb-6">
          <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm leading-relaxed">
            {description}
          </p>
          
          {/* URL Display */}
          <div className={`bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-4 border-2 ${colorTheme === 'purple' ? 'border-purple-200 dark:border-purple-700' : 'border-green-200 dark:border-green-700'}`}>
            <p className="text-sm font-mono text-gray-800 dark:text-gray-200 break-all select-all">
              {url}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="relative space-y-3">
          <button
            onClick={handleCopyAgain}
            className={`w-full px-6 py-3 ${
              isCopied 
                ? (colorTheme === 'purple' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-green-600 hover:bg-green-700')
                : 'bg-blue-600 hover:bg-blue-700'
            } text-white rounded-lg font-bold transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl btn-ripple`}
          >
            {isCopied ? '✅ Copied Again!' : '📋 Copy Link Again'}
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={handleOpenInNewTab}
              className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-all duration-200"
            >
              🌐 Open Link
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-500 transition-all duration-200"
            >
              ✅ Done
            </button>
          </div>
        </div>

        {/* Auto-close indicator */}
        <div className="relative mt-4 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            This dialog will close automatically in a few seconds
          </p>
        </div>
      </div>
    </div>
  );
};

export default URLCopyModal;